import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronRight, ExternalLink, Flame, Plus, Settings2, Trash2 } from 'lucide-react'
import { api } from '@/api/client'
import { useSources } from '@/hooks/useSources'
import ErrorBanner from '@/components/ErrorBanner'
import { relativeTime, formatTime, cn } from '@/lib/utils'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Pagination,
  Select,
} from '@/admin-design/_common'
import type { AdminTopic, AdminTopicItem, TopicWorker, CreateTopicWorkerInput } from '@/api/types'

const PAGE_SIZE = 30

function formatScore(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toFixed(0)
}

function ScoreBadge({ score }: { score: number }) {
  const color = score >= 1000 ? 'text-destructive' : score >= 100 ? 'text-warning' : 'text-muted-foreground'
  return <span className={cn('tabular-nums font-medium text-xs', color)}>{formatScore(score)}</span>
}

function TopicRow({ topic }: { topic: AdminTopic }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const detail = useQuery({
    queryKey: ['admin-topic', topic.id],
    queryFn: () => api.getAdminTopic(topic.id),
    enabled: open,
  })
  const deleteMut = useMutation({
    mutationFn: () => api.deleteAdminTopic(topic.id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['admin-topics'] }) },
  })
  const reclusterMut = useMutation({
    mutationFn: () => api.reclusterTopic(topic.id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['admin-topics'] }) },
  })
  const items = detail.data?.items ?? []
  const sources = Object.entries(topic.source_distribution).sort((a, b) => b[1] - a[1])

  return (
    <Card className="overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start gap-2 px-3 py-2.5 text-left hover:bg-muted/50 transition-colors"
      >
        <span className="shrink-0 mt-0.5 text-muted-foreground">
          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {topic.spike && <Flame size={14} className="text-destructive shrink-0" />}
            {topic.worker_name && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 text-white"
                style={{ backgroundColor: topic.worker_color || '#7c3aed' }}>
                {topic.worker_name}
              </span>
            )}
            {!topic.labeled && (
              <Badge variant="warning" className="text-[10px]">unlabeled</Badge>
            )}
            <span className="text-sm font-medium text-card-foreground line-clamp-1">
              {topic.title || '(untitled)'}
            </span>
          </div>
          {topic.summary && (
            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{topic.summary}</p>
          )}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {topic.keywords.slice(0, 5).map((kw) => (
              <Badge key={kw} className="text-[10px]">{kw}</Badge>
            ))}
          </div>
        </div>

        <div className="shrink-0 flex items-center gap-2 sm:gap-3 text-xs text-muted-foreground">
          <div className="hidden md:flex gap-1">
            {sources.slice(0, 3).map(([slug, count]) => (
              <Badge key={slug} className="text-[10px] tabular-nums rounded">
                {slug}:{count}
              </Badge>
            ))}
          </div>
          <span className="tabular-nums font-medium">{topic.item_count}</span>
          <ScoreBadge score={topic.score} />
          <span className="hidden sm:inline tabular-nums" title={formatTime(topic.created_at)}>
            {topic.created_at ? relativeTime(topic.created_at) : '-'}
          </span>
        </div>
      </button>

      {open && (
        <div className="border-t border-border bg-muted/50 px-4 py-3">
          {detail.isLoading && <p className="text-xs text-muted-foreground">Loading items...</p>}
          {items.length > 0 && (
            <div className="space-y-1">
              <div className="text-[11px] text-muted-foreground mb-1">
                {items.length} member items (top 50 by recency)
              </div>
              {items.map((it) => (
                <TopicItemRow key={it.id} item={it} />
              ))}
            </div>
          )}
          {!detail.isLoading && items.length === 0 && (
            <p className="text-xs text-muted-foreground">No member items.</p>
          )}
          <div className="flex justify-end gap-3 mt-2 pt-2 border-t border-border">
            <Button type="button" variant="link" size="sm"
              onClick={() => { if (confirm('Re-cluster this topic? It will be deleted and member items re-processed.')) reclusterMut.mutate() }}
              loading={reclusterMut.isPending} className="h-auto p-0 text-xs">
              Re-cluster
            </Button>
            <Button type="button" variant="link" size="sm" onClick={() => deleteMut.mutate()}
              loading={deleteMut.isPending} className="h-auto p-0 text-xs text-destructive hover:text-destructive/80">
              Delete
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}

function TopicItemRow({ item }: { item: AdminTopicItem }) {
  return (
    <div className="flex items-center gap-2 py-1 text-xs">
      <Badge className="font-mono text-[10px] hidden sm:inline-flex w-16 text-center truncate rounded">
        {item.source_slug}
      </Badge>
      <a
        href={item.url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex-1 min-w-0 text-foreground hover:text-info hover:underline truncate inline-flex items-center gap-1"
      >
        {item.title}
        <ExternalLink size={10} className="shrink-0 text-muted-foreground" />
      </a>
      <span className="shrink-0 text-muted-foreground tabular-nums text-[10px]" title={`similarity: ${item.similarity.toFixed(3)}`}>
        {(item.similarity * 100).toFixed(0)}%
      </span>
      <span className="shrink-0 text-muted-foreground tabular-nums w-14 text-right hidden sm:block" title={formatTime(item.captured_at)}>
        {relativeTime(item.captured_at)}
      </span>
    </div>
  )
}

// ─── Worker management ──────────────────────────────────────────────────

// BGE-M3 Q8_0 기준 권장 threshold. 짧은 입력일수록 같은 주제 pair가 더 근접하게
// 묶이므로 threshold를 높게, 긴 입력일수록 body 노이즈/표현 다양성 때문에 낮게.
// embed_fields 변경 시 기본값이 자동으로 이 값으로 바뀐다 (사용자가 이미 수정한 값은 유지).
const EMBED_OPTIONS = [
  { value: 'title',          label: 'Title only',          defaultThreshold: 0.78 },
  { value: 'title_body',     label: 'Title + Body (200)',  defaultThreshold: 0.74 },
  { value: 'title_body_300', label: 'Title + Body (300)',  defaultThreshold: 0.72 },
  { value: 'title_body_512', label: 'Title + Body (512)',  defaultThreshold: 0.70 },
]

function recommendedThreshold(embedFields: string): number {
  return EMBED_OPTIONS.find((o) => o.value === embedFields)?.defaultThreshold ?? 0.72
}

function embedSelectOptions() {
  return EMBED_OPTIONS.map((o) => ({
    value: o.value,
    label: `${o.label} (권장 ${o.defaultThreshold})`,
  }))
}

function useTopicWorkers() {
  return useQuery({ queryKey: ['topic-workers'], queryFn: api.listTopicWorkers })
}

// ─── Source picker (dropdown with groups/tags/individual) ────────────────

import type { Source } from '@/api/types'

function SourcePicker({ sources, selected, onChange }: {
  sources: Source[]
  selected: Set<string>
  onChange: (next: Set<string>) => void
}) {
  const [open, setOpen] = useState(false)

  // Group by kind
  const groups = useMemo(() => {
    const map = new Map<string, Source[]>()
    for (const s of sources) {
      const g = s.kind || 'other'
      if (!map.has(g)) map.set(g, [])
      map.get(g)!.push(s)
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [sources])

  // All tags
  const allTags = useMemo(() =>
    [...new Set(sources.flatMap((s) => s.tags ?? []))].sort(),
    [sources])

  const toggle = (slug: string) => {
    const n = new Set(selected); n.has(slug) ? n.delete(slug) : n.add(slug); onChange(n)
  }
  const selectGroup = (slugs: string[]) => {
    const n = new Set(selected); slugs.forEach((s) => n.add(s)); onChange(n)
  }
  const deselectGroup = (slugs: string[]) => {
    const n = new Set(selected); slugs.forEach((s) => n.delete(s)); onChange(n)
  }
  const selectByTag = (tag: string) => {
    const slugs = sources.filter((s) => (s.tags ?? []).includes(tag)).map((s) => s.slug)
    selectGroup(slugs)
  }

  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="w-full rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring text-left flex items-center justify-between">
        <span className="truncate">
          {selected.size === 0 ? 'Select sources...' : `${selected.size} sources selected`}
        </span>
        <ChevronDown size={12} className={cn('transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full sm:w-80 border border-border rounded-lg bg-card shadow-lg max-h-72 overflow-y-auto">
          {/* Quick select by tag */}
          {allTags.length > 0 && (
            <div className="px-2 py-1.5 border-b border-border">
              <span className="text-[10px] text-muted-foreground uppercase tracking-wider">Tags</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {allTags.map((tag) => (
                  <button key={tag} type="button" onClick={() => selectByTag(tag)}
                    className="px-1.5 py-0.5 rounded text-[10px] border border-border bg-card text-muted-foreground hover:bg-info-soft hover:text-info-soft-foreground hover:border-info-soft">
                    +{tag}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Groups with checkboxes */}
          {groups.map(([groupName, groupSources]) => {
            const groupSlugs = groupSources.map((s) => s.slug)
            const allChecked = groupSlugs.every((s) => selected.has(s))
            const someChecked = groupSlugs.some((s) => selected.has(s))
            return (
              <div key={groupName} className="border-b border-border last:border-0">
                <label className="flex items-center gap-2 px-2 py-1.5 hover:bg-muted/50 cursor-pointer">
                  <input type="checkbox" checked={allChecked}
                    ref={(el) => { if (el) el.indeterminate = someChecked && !allChecked }}
                    onChange={() => allChecked ? deselectGroup(groupSlugs) : selectGroup(groupSlugs)}
                    className="h-3.5 w-3.5 shrink-0" />
                  <span className="text-xs font-medium text-foreground">{groupName}</span>
                  <span className="text-[10px] text-muted-foreground ml-auto">{groupSources.length}</span>
                </label>
                <div className="pl-6 pb-1">
                  {groupSources.sort((a, b) => a.slug.localeCompare(b.slug)).map((s) => (
                    <label key={s.slug} className="flex items-center gap-2 px-2 py-0.5 hover:bg-muted/30 cursor-pointer rounded">
                      <input type="checkbox" checked={selected.has(s.slug)}
                        onChange={() => toggle(s.slug)} className="h-3 w-3 shrink-0" />
                      <span className="text-[11px] text-foreground font-mono">{s.slug}</span>
                      <span className="text-[10px] text-muted-foreground truncate ml-auto">{s.display_name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )
          })}

          {/* Actions */}
          <div className="flex items-center gap-2 px-2 py-1.5 border-t border-border bg-muted/30">
            <button type="button" onClick={() => onChange(new Set(sources.map((s) => s.slug)))}
              className="text-[10px] text-info hover:underline">All</button>
            <button type="button" onClick={() => onChange(new Set())}
              className="text-[10px] text-muted-foreground hover:underline">None</button>
            <button type="button" onClick={() => setOpen(false)}
              className="ml-auto text-[10px] text-foreground hover:underline">Done</button>
          </div>
        </div>
      )}

      {/* Selected pills preview */}
      {selected.size > 0 && (
        <div className="flex flex-wrap gap-0.5 mt-1">
          {[...selected].sort().slice(0, 8).map((s) => (
            <Badge key={s} variant="info" className="text-[10px] rounded">{s}</Badge>
          ))}
          {selected.size > 8 && <span className="text-[10px] text-muted-foreground">+{selected.size - 8}</span>}
        </div>
      )}
    </div>
  )
}

function WorkersPanel() {
  const qc = useQueryClient()
  const workers = useTopicWorkers()
  const [showNew, setShowNew] = useState(false)

  const deleteMut = useMutation({
    mutationFn: (id: number) => api.deleteTopicWorker(id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['topic-workers'] }) },
  })
  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: number; input: Parameters<typeof api.updateTopicWorker>[1] }) =>
      api.updateTopicWorker(id, input),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['topic-workers'] }) },
  })
  const reclusterMut = useMutation({
    mutationFn: (id: number) => api.reclusterWorker(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['topic-workers'] })
      void qc.invalidateQueries({ queryKey: ['admin-topics'] })
    },
  })

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
          <Settings2 size={14} /> Topic Workers
        </h2>
        <Button type="button" variant="primary" size="sm" onClick={() => setShowNew((v) => !v)}
          className="bg-foreground text-background hover:opacity-80">
          <Plus size={12} /> New Worker
        </Button>
      </div>

      {showNew && <NewWorkerForm onClose={() => setShowNew(false)} />}

      {(workers.data ?? []).length === 0 && !showNew && (
        <Card>
          <EmptyState
            title="No workers configured"
            description="Create a worker to define which sources get clustered together."
          />
        </Card>
      )}

      {(workers.data ?? []).map((w) => (
        <WorkerCard key={w.id} worker={w}
          onUpdate={(input) => updateMut.mutate({ id: w.id, input })}
          onRecluster={() => reclusterMut.mutate(w.id)}
          onDelete={() => deleteMut.mutate(w.id)} />
      ))}
    </section>
  )
}

const WORKER_COLORS = [
  '#7c3aed', // violet
  '#2563eb', // blue
  '#059669', // emerald
  '#d97706', // amber
  '#dc2626', // red
  '#db2777', // pink
  '#0891b2', // cyan
  '#4f46e5', // indigo
  '#65a30d', // lime
  '#9333ea', // purple
]

function ColorPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <div className="flex gap-1 items-center">
      {WORKER_COLORS.map((c) => (
        <button key={c} type="button" onClick={() => onChange(c)}
          className={cn('w-5 h-5 rounded-full border-2 transition-all',
            value === c ? 'border-foreground scale-110' : 'border-transparent hover:scale-105')}
          style={{ backgroundColor: c }} />
      ))}
    </div>
  )
}

function NewWorkerForm({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const create = useMutation({
    mutationFn: (input: CreateTopicWorkerInput) => api.createTopicWorker(input),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['topic-workers'] }); onClose() },
  })
  const allSources = useSources().data ?? []
  const [name, setName] = useState('')
  const [color, setColor] = useState(WORKER_COLORS[0])
  const [selectedSlugs, setSelectedSlugs] = useState<Set<string>>(new Set())
  const [embedFields, setEmbedFields] = useState('title_body')
  // threshold가 이전 embedFields의 '추천값'과 같으면 새 추천값으로 동기화.
  // 사용자가 수동으로 다른 값을 입력했으면 유지. -> thresholdTouched로 판별.
  const [threshold, setThreshold] = useState(String(recommendedThreshold('title_body')))
  const [thresholdTouched, setThresholdTouched] = useState(false)

  const onEmbedChange = (v: string) => {
    setEmbedFields(v)
    if (!thresholdTouched) setThreshold(String(recommendedThreshold(v)))
  }

  return (
    <Card className="p-3 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-muted-foreground">Worker 이름</span>
          <Input type="text" placeholder="예: 국내뉴스" value={name} onChange={(e) => setName(e.target.value)}
            className="h-auto px-2 py-1 text-xs" autoFocus />
        </label>

        <div className="flex flex-col gap-1">
          <span className="text-[11px] text-muted-foreground">뱃지 색상</span>
          <ColorPicker value={color} onChange={setColor} />
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-muted-foreground">임베딩 입력 (제목만 / 제목+본문 N자)</span>
          <Select value={embedFields} onValueChange={onEmbedChange}
            options={embedSelectOptions()} className="h-auto px-2 py-1 text-xs" />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-muted-foreground">
            Assign Threshold (cosine, 이 값 이상 → 기존 토픽 병합)
          </span>
          <Input type="number" step="0.01" min="0.3" max="0.95" value={threshold}
            onChange={(e) => { setThreshold(e.target.value); setThresholdTouched(true) }}
            className="h-auto px-2 py-1 text-xs font-mono" />
        </label>
      </div>

      <label className="flex flex-col gap-1">
        <span className="text-[11px] text-muted-foreground">수집 대상 소스 (이 worker가 묶을 소스들)</span>
        <SourcePicker sources={allSources} selected={selectedSlugs} onChange={setSelectedSlugs} />
      </label>

      {create.error && <ErrorBanner error={create.error} />}
      <div className="flex gap-1 justify-end">
        <Button type="button" variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
        <Button type="button" variant="primary" size="sm"
          disabled={!name.trim() || selectedSlugs.size === 0}
          loading={create.isPending}
          onClick={() => create.mutate({
            name: name.trim(),
            color,
            source_slugs: [...selectedSlugs],
            embed_fields: embedFields,
            assign_threshold: parseFloat(threshold) || recommendedThreshold(embedFields),
          })}
          className="bg-foreground text-background hover:opacity-80">
          Create
        </Button>
      </div>
    </Card>
  )
}

function WorkerCard({ worker: w, onUpdate, onDelete, onRecluster }: {
  worker: TopicWorker
  onUpdate: (input: Parameters<typeof api.updateTopicWorker>[1]) => void
  onDelete: () => void
  onRecluster: () => void
}) {
  const allSources = useSources().data ?? []
  const [editing, setEditing] = useState(false)
  const [selectedSlugs, setSelectedSlugs] = useState<Set<string>>(new Set(w.source_slugs))
  const [embedFields, setEmbedFields] = useState(w.embed_fields)
  const [threshold, setThreshold] = useState(String(w.assign_threshold))
  const [thresholdTouched, setThresholdTouched] = useState(false)

  const onEmbedChange = (v: string) => {
    setEmbedFields(v)
    if (!thresholdTouched) setThreshold(String(recommendedThreshold(v)))
  }

  return (
    <Card className="overflow-visible">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <button type="button" onClick={() => onUpdate({ enabled: !w.enabled })}
          className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 transition-colors',
            w.enabled ? 'bg-emerald-500 text-white hover:bg-emerald-600' : 'bg-muted text-muted-foreground hover:bg-muted/80')}>
          {w.enabled ? 'ON' : 'OFF'}
        </button>
        {w.color && <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: w.color }} />}
        <span className="text-sm font-medium text-foreground">{w.name}</span>
        <div className="hidden sm:flex gap-0.5">
          {w.source_slugs.slice(0, 4).map((s) => (
            <Badge key={s} className="text-[10px] rounded">{s}</Badge>
          ))}
          {w.source_slugs.length > 4 && <span className="text-[10px] text-muted-foreground">+{w.source_slugs.length - 4}</span>}
        </div>
        <span className="sm:hidden text-[10px] text-muted-foreground">{w.source_slugs.length} sources</span>
        <div className="ml-auto flex items-center gap-2 text-xs text-muted-foreground">
          <span className="hidden sm:inline">{EMBED_OPTIONS.find((o) => o.value === w.embed_fields)?.label ?? w.embed_fields}</span>
          <span className="font-mono hidden sm:inline">{w.assign_threshold}</span>
          <span className="tabular-nums">{w.topic_count}</span>
          <button type="button" onClick={() => setEditing((v) => !v)} className="hover:text-foreground" title="Edit">
            <Settings2 size={13} />
          </button>
          <button type="button"
            onClick={() => { if (confirm(`Re-cluster "${w.name}"? All topics will be deleted and items re-processed.`)) onRecluster() }}
            className="text-[10px] hover:text-warning hidden sm:inline" title="Re-cluster from scratch">
            Re-cluster
          </button>
          <button type="button" onClick={onDelete} className="hover:text-destructive" title="Delete worker">
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {editing && (
        <div className="border-t border-border bg-muted/30 px-3 py-3 space-y-3">
          <div className="flex flex-col gap-1 mb-2">
            <span className="text-[11px] text-muted-foreground">뱃지 색상</span>
            <ColorPicker value={w.color || WORKER_COLORS[0]} onChange={(c) => onUpdate({ color: c })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-[11px] text-muted-foreground">임베딩 입력 (제목만 / 제목+본문 N자)</span>
              <Select value={embedFields} onValueChange={onEmbedChange}
                options={embedSelectOptions()} className="h-auto px-2 py-1 text-xs" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[11px] text-muted-foreground">
                Assign Threshold (cosine, 이 값 이상 → 기존 토픽 병합)
              </span>
              <Input type="number" step="0.01" min="0.3" max="0.95" value={threshold}
                onChange={(e) => { setThreshold(e.target.value); setThresholdTouched(true) }}
                className="h-auto px-2 py-1 text-xs font-mono" />
            </label>
          </div>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-muted-foreground">수집 대상 소스</span>
            <SourcePicker sources={allSources} selected={selectedSlugs} onChange={setSelectedSlugs} />
          </label>
          <div className="flex justify-end">
            <Button type="button" variant="primary" size="sm"
              onClick={() => {
                onUpdate({
                  source_slugs: [...selectedSlugs],
                  embed_fields: embedFields,
                  assign_threshold: parseFloat(threshold) || recommendedThreshold(embedFields),
                })
                setEditing(false)
              }}
              className="bg-foreground text-background hover:opacity-80">
              Save
            </Button>
          </div>
        </div>
      )}
    </Card>
  )
}

// ─── Topics list ────────────────────────────────────────────────────────

export default function Topics() {
  const [sp, setSp] = useSearchParams()
  const search = sp.get('q') || ''
  const spikeOnly = sp.get('spike') === '1'
  const labeledFilter = sp.get('labeled') || ''
  const workerFilter = sp.get('worker') || ''
  const page = parseInt(sp.get('page') || '0', 10)
  const [searchInput, setSearchInput] = useState(search)

  const setFilter = (updates: Record<string, string>) => {
    const next = new URLSearchParams(sp)
    for (const [k, v] of Object.entries(updates)) {
      if (v && !(k === 'page' && v === '0')) next.set(k, v)
      else next.delete(k)
    }
    // Reset page when filters change (unless page itself is being set)
    if (!('page' in updates)) next.delete('page')
    setSp(next, { replace: true })
  }

  const qc = useQueryClient()
  const workers = useTopicWorkers()
  const bulkDelete = useMutation({
    mutationFn: (worker: string) => api.bulkDeleteTopics(worker),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['admin-topics'] }) },
  })
  const topics = useQuery({
    queryKey: ['admin-topics', { q: search, spike: spikeOnly, labeled: labeledFilter, worker: workerFilter, limit: PAGE_SIZE, offset: page * PAGE_SIZE }],
    queryFn: () => api.listAdminTopics({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      q: search || undefined,
      spike: spikeOnly || undefined,
      labeled: labeledFilter || undefined,
      worker: workerFilter || undefined,
    }),
    refetchInterval: 30_000,
  })

  const total = topics.data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const rows = topics.data?.data ?? []

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setFilter({ q: searchInput.trim() })
  }

  const workerOptions = [
    { label: 'All workers', value: '' },
    { label: 'Unassigned', value: 'none' },
    ...((workers.data ?? []).map((w) => ({ label: w.name, value: String(w.id) }))),
  ]

  const labeledOptions = [
    { label: 'All', value: '' },
    { label: 'Unlabeled', value: '0' },
    { label: 'Labeled', value: '1' },
  ]

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Topics</h1>

      <WorkersPanel />

      <div className="flex flex-wrap items-center gap-2">
        <Select value={workerFilter} onValueChange={(v) => setFilter({ worker: v })}
          options={workerOptions} className="max-w-[130px] sm:max-w-[160px]" />
        {workerFilter && (
          <Button type="button" variant="secondary" size="sm"
            loading={bulkDelete.isPending}
            onClick={() => {
              if (confirm(`Delete all topics from "${workerFilter === 'none' ? 'Unassigned' : workers.data?.find((w) => String(w.id) === workerFilter)?.name}"?`)) {
                bulkDelete.mutate(workerFilter)
              }
            }}
            className="border-destructive-soft text-destructive hover:bg-destructive-soft">
            Delete all
          </Button>
        )}

        <form onSubmit={onSearch} className="flex gap-1">
          <Input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search..." className="w-32 sm:w-40" />
          <Button type="submit" variant="secondary" size="sm">Search</Button>
          {search && (
            <Button type="button" variant="ghost" size="sm" onClick={() => { setSearchInput(''); setFilter({ q: '' }) }}>
              Clear
            </Button>
          )}
        </form>

        <Select value={labeledFilter} onValueChange={(v) => setFilter({ labeled: v })}
          options={labeledOptions} className="max-w-[120px]" />

        <label className="inline-flex items-center gap-1.5 cursor-pointer text-sm">
          <input type="checkbox" checked={spikeOnly} onChange={(e) => setFilter({ spike: e.target.checked ? '1' : '' })}
            className="h-3.5 w-3.5" />
          <Flame size={14} className="text-destructive" />
          <span className="text-muted-foreground">Spikes</span>
        </label>

        <span className="ml-auto text-xs text-muted-foreground">{total.toLocaleString('ko-KR')} topics</span>
        {topics.isFetching && <span className="text-xs text-muted-foreground animate-pulse">syncing...</span>}
      </div>

      {topics.error && <ErrorBanner error={topics.error} />}

      {topics.isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i} className="p-3">
              <div className="h-4 w-2/3 rounded bg-muted animate-pulse" />
            </Card>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <Card>
          <EmptyState
            title={search || spikeOnly ? 'No matching topics.' : 'No topics yet'}
            description={search || spikeOnly ? undefined : 'Enable the Topics Pipeline in Settings to start clustering.'}
          />
        </Card>
      ) : (
        <div className="space-y-2">
          {rows.map((t) => <TopicRow key={t.id} topic={t} />)}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
          <span className="text-xs sm:text-sm">
            {(page * PAGE_SIZE + 1).toLocaleString('ko-KR')}–
            {Math.min((page + 1) * PAGE_SIZE, total).toLocaleString('ko-KR')}
            {' / '}{total.toLocaleString('ko-KR')}
          </span>
          <Pagination
            page={page + 1}
            totalPages={totalPages}
            onChange={(p) => setFilter({ page: String(p - 1) })}
            disabled={topics.isFetching}
          />
        </div>
      )}
    </div>
  )
}
